import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import jsQR from 'jsqr';
import { db, auth } from '../../../firebase/config';
import { callClaude } from '../../../utils/claudeProxy';
import { signInAnonymously } from 'firebase/auth';
import { doc, getDoc, addDoc, getDocs, collection, query, where, serverTimestamp } from 'firebase/firestore';
import {
  Box, Typography, Button, CircularProgress, Paper, IconButton,
  TextField, Alert, MenuItem, Select, FormControl, InputLabel, Chip,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import EditIcon from '@mui/icons-material/Edit';
import ContentCutIcon from '@mui/icons-material/ContentCut';

const accent = '#E05500';
const bg = '#000';
const surface = '#1c1c1e';

// ── QR decode: BarcodeDetector → jsQR fallback ────────────────────────────────
async function decodeQRFromFile(file) {
  // Method 1: native BarcodeDetector (Chrome/Android)
  if ('BarcodeDetector' in window) {
    try {
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const bitmap = await createImageBitmap(file);
      const codes = await detector.detect(bitmap);
      if (codes.length > 0) return { value: codes[0].rawValue, method: 'BarcodeDetector' };
    } catch { /* fall through */ }
  }

  // Method 2: jsQR (universal fallback)
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });
    if (code) return { value: code.data, method: 'jsQR' };
  } catch { /* fall through */ }

  return null;
}

// Parse q and g from a scan URL
function parseQRUrl(raw) {
  try {
    const url = new URL(raw);
    const q = parseInt(url.searchParams.get('q') ?? '', 10);
    const g = url.searchParams.get('g') ?? null;
    if (!isNaN(q) && g) return { qIdx: q, groupId: g };
  } catch { /* not a URL */ }
  return null;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function WorkshopScan() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('s');

  // q and g from URL are optional — may be filled later via QR decode or AI
  const urlQIdx   = parseInt(searchParams.get('q') ?? '', 10);
  const urlGId    = searchParams.get('g');

  const [session, setSession]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  // Resolved identity (from URL params, QR decode, or AI)
  const [resolvedQIdx, setResolvedQIdx] = useState(isNaN(urlQIdx) ? null : urlQIdx);
  const [resolvedGId,  setResolvedGId]  = useState(urlGId ?? null);

  // Manual override selector
  const [manualQIdx, setManualQIdx] = useState('');
  const [manualGId,  setManualGId]  = useState('');
  const [showManual, setShowManual] = useState(false);

  // Photo + analysis state
  const [imageFile, setImageFile]     = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [qrStatus, setQrStatus]       = useState(null); // null | 'detecting' | 'found' | 'not_found'
  const [analyzing, setAnalyzing]     = useState(false);
  const [parsedAnswers, setParsedAnswers]       = useState(null);
  const [participantNames, setParticipantNames] = useState([]);
  const [verificationWarning, setVerificationWarning] = useState('');

  // Namn som redan sparats för denna grupp (från tidigare skanningar)
  const [existingGroupNames, setExistingGroupNames] = useState([]);

  // Nytt namn-input för redigerbar deltagarsektion
  const [newNameInput, setNewNameInput] = useState('');

  // Save state
  const [saving, setSaving]     = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [done, setDone]         = useState(false);

  const fileRef = useRef();

  useEffect(() => {
    if (!sessionId) { setError('Ogiltig länk — session saknas.'); setLoading(false); return; }
    getDoc(doc(db, 'workshops', sessionId)).then(snap => {
      if (!snap.exists()) { setError('Workshopen hittades inte.'); }
      else { setSession({ id: snap.id, ...snap.data() }); }
      setLoading(false);
    });
  }, [sessionId]);

  const questions = session?.config?.questions || [];
  const groups    = session?.config?.groups    || [];
  const question  = resolvedQIdx !== null ? questions[resolvedQIdx] : null;
  const group     = resolvedGId  ? groups.find(g => g.id === resolvedGId) : null;
  const hasNextQuestion = resolvedQIdx !== null && resolvedQIdx + 1 < questions.length;
  const identityKnown = resolvedQIdx !== null && resolvedGId !== null;

  // Hämta befintliga deltagarnamn för gruppen när vi vet vilken grupp det gäller
  useEffect(() => {
    if (!sessionId || !resolvedGId) return;
    getDocs(query(
      collection(db, 'workshops', sessionId, 'responses'),
      where('groupId', '==', resolvedGId),
      where('isHybridScan', '==', true),
    )).then(snap => {
      const names = [...new Set(
        snap.docs.flatMap(d => d.data().participantNames || []).filter(n => n?.trim())
      )];
      setExistingGroupNames(names);
    });
  }, [sessionId, resolvedGId]);

  // ── Photo handler — auto-run QR decode ─────────────────────────────────────
  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setParsedAnswers(null);
    setVerificationWarning('');
    setShowManual(false);

    // Only try QR if we don't already have both q and g from URL
    if (resolvedQIdx !== null && resolvedGId !== null) return;

    setQrStatus('detecting');
    const result = await decodeQRFromFile(file);
    if (result) {
      const parsed = parseQRUrl(result.value);
      if (parsed) {
        setResolvedQIdx(parsed.qIdx);
        setResolvedGId(parsed.groupId);
        setQrStatus('found');
        return;
      }
    }
    setQrStatus('not_found');
  }

  // ── AI analysis ─────────────────────────────────────────────────────────────
  async function analyze() {
    if (!imageFile) return;
    setAnalyzing(true);
    setParsedAnswers(null);
    setVerificationWarning('');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });

      const mediaType = imageFile.type || 'image/jpeg';
      const qType = question?.type || 'fritext';

      let instructions = 'Extract all handwritten text, notes, words, and phrases from the answer area of this workshop sheet.';
      if (qType === 'skala')   instructions += ' The question is a 1–5 scale. Look for circled or marked numbers.';
      if (qType === 'flerval') instructions += ` The options are: ${(question?.options || []).join(', ')}. Look for circled/checked options.`;
      if (qType === 'ordmoln') instructions += ' Focus on individual keywords and short phrases.';

      const knowIdentity = resolvedQIdx !== null && resolvedGId !== null;
      const identityBlock = knowIdentity
        ? `Expected group: "${group?.name ?? resolvedGId}"
Expected question number: ${resolvedQIdx + 1}
Question text: "${question?.text ?? ''}"`
        : `Available groups: ${groups.map(g => `"${g.name}"`).join(', ')}
Available questions: ${questions.map((q, i) => `${i + 1}. "${q.text}"`).join('; ')}`;

      const verifyInstruction = knowIdentity
        ? `STEP 1 — Verify: Read the GROUP NAME and QUESTION NUMBER from the sheet header. Compare with expected values above. Report any mismatch.`
        : `STEP 1 — Identify: Read the GROUP NAME and QUESTION NUMBER from the sheet header. Match them to the available groups and questions listed above.`;

      const prompt = `${instructions}

Workshop: "${session?.title}"
${identityBlock}

${verifyInstruction}

STEP 2 — Extract handwritten answers from the large answer area: Each bullet point, line, or distinct phrase is a separate answer. Do NOT include names here.

STEP 3 — Extract participant names from the DELTAGARE section in the footer (the narrow column with short lines at the bottom of the sheet). These are full names written by participants — extract only what is clearly a person's name, ignore empty lines.

Return ONLY valid JSON (no markdown):
{
  "sheetGroup": "<group name from sheet header, or null>",
  "sheetQuestion": <question number as integer from sheet header, or null>,
  "participantNames": ["<First Last>", ...],
  "answers": ["<answer 1>", "<answer 2>"]
}`;

      const data = await callClaude({
        model: 'claude-sonnet-4-6',
        max_tokens: 1000,
        messages: [{
          role: 'user',
          content: [
            { type: 'image', source: { type: 'base64', media_type: mediaType, data: base64 } },
            { type: 'text', text: prompt },
          ],
        }],
      });
      const raw = data.content?.[0]?.text || '{}';
      const clean = raw.replace(/```json|```/g, '').trim();

      let parsed;
      try { parsed = JSON.parse(clean); } catch { parsed = {}; }

      const answers          = Array.isArray(parsed.answers)          ? parsed.answers          : [];
      const sheetGroup       = parsed.sheetGroup       ?? null;
      const sheetQNum        = parsed.sheetQuestion    ?? null;
      const scannedNames     = Array.isArray(parsed.participantNames) ? parsed.participantNames : [];

      setParsedAnswers(answers);
      setParticipantNames(scannedNames);

      // If identity was unknown, try to resolve from AI reading
      if (!identityKnown) {
        let aiQIdx = null;
        let aiGId  = null;

        if (sheetQNum !== null) {
          const idx = sheetQNum - 1;
          if (idx >= 0 && idx < questions.length) aiQIdx = idx;
        }
        if (sheetGroup) {
          const match = groups.find(g =>
            g.name.trim().toLowerCase() === sheetGroup.trim().toLowerCase()
          );
          if (match) aiGId = match.id;
        }

        if (aiQIdx !== null) setResolvedQIdx(aiQIdx);
        if (aiGId)           setResolvedGId(aiGId);

        if (aiQIdx === null || aiGId === null) {
          // AI couldn't fully identify — show manual selector
          setShowManual(true);
        }
        return;
      }

      // Identity was already known — verify it
      const groupMismatch = sheetGroup && group?.name &&
        sheetGroup.trim().toLowerCase() !== group.name.trim().toLowerCase();
      const questionMismatch = sheetQNum !== null && sheetQNum !== resolvedQIdx + 1;

      if (groupMismatch) {
        setVerificationWarning(`OBS: AI hittade grupp "${sheetGroup}" på bladet men du fotograferar för grupp "${group?.name}". Kontrollera att du har rätt blad!`);
      } else if (questionMismatch) {
        setVerificationWarning(`OBS: Bladet verkar vara fråga ${sheetQNum} men du fotograferar för fråga ${resolvedQIdx + 1}. Kontrollera att du har rätt blad!`);
      }
    } catch {
      setError('Kunde inte analysera bilden. Försök igen.');
    } finally {
      setAnalyzing(false);
    }
  }

  // ── Answer editing ───────────────────────────────────────────────────────────
  function updateAnswer(idx, value) { setParsedAnswers(prev => prev.map((a, i) => i === idx ? value : a)); }
  function removeAnswer(idx)        { setParsedAnswers(prev => prev.filter((_, i) => i !== idx)); }
  function addEmptyAnswer()         { setParsedAnswers(prev => [...prev, '']); }

  function splitAnswer(idx) {
    const parts = parsedAnswers[idx]
      .split(/,\s*|\s*\/\s*|\s*;\s*|\n+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
    if (parts.length < 2) return; // nothing to split
    setParsedAnswers(prev => [
      ...prev.slice(0, idx),
      ...parts,
      ...prev.slice(idx + 1),
    ]);
  }

  function canSplit(ans) {
    return /[,/;\n]/.test(ans) &&
      ans.split(/,\s*|\s*[/]\s*|\s*;\s*|\n+/).filter(s => s.trim()).length >= 2;
  }

  // ── Apply manual selection ───────────────────────────────────────────────────
  function applyManual() {
    if (manualQIdx !== '' && manualGId) {
      setResolvedQIdx(Number(manualQIdx));
      setResolvedGId(manualGId);
      setShowManual(false);
    }
  }

  // ── Save ─────────────────────────────────────────────────────────────────────
  async function saveAnswers() {
    const toSave = (parsedAnswers || []).filter(a => a.trim() !== '');
    if (!toSave.length || resolvedQIdx === null || !resolvedGId) return;
    setSaving(true);
    try {
      // Externa användare utan MS SSO måste ha ett Firebase auth-token för att
      // kunna skriva till Firestore. Anonym inloggning räcker — ingen e-post krävs.
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }
      const grp = groups.find(g => g.id === resolvedGId);
      // Använd namn från detta blad, eller fall tillbaka på namn från tidigare skanningar för gruppen
      const namesToStore = participantNames.filter(n => n.trim()).length > 0
        ? participantNames.filter(n => n.trim())
        : existingGroupNames;
      for (let i = 0; i < toSave.length; i++) {
        await addDoc(collection(db, 'workshops', sessionId, 'responses'), {
          userId: 'hybrid_scan',
          displayName: `Hybrid-svar ${i + 1} (${grp?.name || ''})`,
          groupId: resolvedGId,
          groupName: grp?.name || '',
          answers: { [resolvedQIdx]: toSave[i] },
          participantNames: namesToStore,
          isHybridScan: true,
          submittedAt: serverTimestamp(),
        });
      }
      setSavedCount(toSave.length);
      setDone(true);
    } catch {
      setError('Kunde inte spara svaren. Försök igen.');
    } finally {
      setSaving(false);
    }
  }

  function resetForMoreSheets() {
    setImageFile(null);
    setImagePreview(null);
    setParsedAnswers(null);
    setParticipantNames([]);
    setVerificationWarning('');
    setQrStatus(null);
    setShowManual(false);
    setSavedCount(0);
    setDone(false);
  }

  function goToNextQuestion() {
    window.location.href = `/workshop-scan?s=${sessionId}&q=${resolvedQIdx + 1}&g=${resolvedGId}`;
  }

  // ── Done screen ──────────────────────────────────────────────────────────────
  if (done) {
    const grp = groups.find(g => g.id === resolvedGId);
    const q   = resolvedQIdx !== null ? questions[resolvedQIdx] : null;
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        {!hasNextQuestion ? (
          <>
            <Typography fontSize="4rem" mb={1} lineHeight={1}>🎉</Typography>
            <Typography variant="h4" color="#fff" fontWeight={800} mb={0.75} textAlign="center">
              TACK för ditt bidrag!
            </Typography>
            <Typography color="#888" fontSize="1rem" textAlign="center" mb={4}>
              Alla frågor är skannade och sparade.
            </Typography>
          </>
        ) : (
          <>
            <CheckCircleOutlineIcon sx={{ fontSize: 72, color: '#4ade80', mb: 3 }} />
            <Typography variant="h5" color="#fff" fontWeight={700} mb={1} textAlign="center">
              {savedCount} svar sparade!
            </Typography>
          </>
        )}
        <Box sx={{ bgcolor: surface, border: '1px solid rgba(84,84,88,0.5)', borderRadius: 2, px: 3, py: 2, mb: 4, textAlign: 'center', maxWidth: 400 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: grp?.color || accent }} />
            <Typography fontWeight={700} color={grp?.color || accent}>{grp?.name}</Typography>
          </Box>
          <Typography color="#ccc" fontSize="0.88rem">
            Fråga {(resolvedQIdx ?? 0) + 1}: <em>{q?.text}</em>
          </Typography>
        </Box>

        {hasNextQuestion ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 380 }}>
            <Button variant="contained" fullWidth endIcon={<ArrowForwardIcon />} onClick={goToNextQuestion}
              sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 700, py: 1.5 }}>
              Scanna fråga {(resolvedQIdx ?? 0) + 2} för {grp?.name}
            </Button>
            <Button variant="outlined" fullWidth startIcon={<AddPhotoAlternateIcon />} onClick={resetForMoreSheets}
              sx={{ borderColor: 'rgba(84,84,88,0.6)', color: '#ccc', textTransform: 'none', py: 1.5 }}>
              Skanna baksidan eller ett blad till för samma fråga
            </Button>
            <Typography fontSize="0.75rem" color="#555" textAlign="center" mt={-0.5}>
              Ingen QR-kod behövs — lag och fråga är redan kopplade
            </Typography>
          </Box>
        ) : (
          <Typography color="#555" fontSize="0.88rem" textAlign="center">
            Du kan stänga det här fönstret och återgå till workshopen.
          </Typography>
        )}
      </Box>
    );
  }

  // ── Loading / error ──────────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );
  if (error && !session) return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Typography color="#ef4444" textAlign="center">{error}</Typography>
    </Box>
  );

  // ── Main UI ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg, color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
      <Box sx={{ width: '100%', maxWidth: 520, mt: { xs: 1, md: 4 } }}>

        {/* Session header */}
        <Typography color={accent} fontSize="0.82rem" fontWeight={700} letterSpacing={1} mb={1.5}>
          {session?.title}
        </Typography>

        {/* Identity — stor och tydlig för mobil */}
        {identityKnown ? (
          <Box sx={{ mb: 2.5 }}>
            {/* Gruppetikett */}
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25, bgcolor: (group?.color || accent) + '22', border: `2px solid ${group?.color || accent}55`, borderRadius: 1.5, px: 2, py: 0.75, mb: 1.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: group?.color || accent }} />
              <Typography fontSize="1.25rem" fontWeight={800} color={group?.color || accent} lineHeight={1}>
                {group?.name}
              </Typography>
            </Box>
            {/* Frågetext */}
            <Typography fontSize="1.4rem" fontWeight={700} color="#fff" lineHeight={1.25} mb={0.5}>
              Fråga {resolvedQIdx + 1}
            </Typography>
            <Typography fontSize="1.1rem" color="#ccc" lineHeight={1.4} mb={0.5}>
              {question?.text}
            </Typography>
            <IconButton size="small" onClick={() => setShowManual(v => !v)} sx={{ color: '#444', '&:hover': { color: '#aaa' }, p: 0.5, mt: 0.25 }}>
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ) : (
          <Typography color="#888" fontSize="1rem" mb={2.5}>
            Fotografera ett blad — lag och fråga identifieras automatiskt via QR-kod eller AI.
          </Typography>
        )}

        {/* Manual override selector */}
        {showManual && (
          <Paper sx={{ bgcolor: surface, border: '1px solid rgba(84,84,88,0.6)', borderRadius: 2, p: 2, mb: 2 }}>
            <Typography fontSize="0.82rem" color="#aaa" mb={1.5}>
              {parsedAnswers !== null
                ? 'AI kunde inte identifiera blad — välj manuellt:'
                : 'Ändra lag / fråga:'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ color: '#888' }}>Lag</InputLabel>
                <Select value={manualGId} label="Lag" onChange={e => setManualGId(e.target.value)}
                  sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(84,84,88,0.6)' } }}>
                  {groups.map(g => (
                    <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ color: '#888' }}>Fråga</InputLabel>
                <Select value={manualQIdx} label="Fråga" onChange={e => setManualQIdx(e.target.value)}
                  sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(84,84,88,0.6)' } }}>
                  {questions.map((q, i) => (
                    <MenuItem key={i} value={i}>Fråga {i + 1}: {q.text}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={applyManual} disabled={manualQIdx === '' || !manualGId}
                sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}>
                OK
              </Button>
            </Box>
          </Paper>
        )}

        {/* QR decode status badge */}
        {qrStatus === 'detecting' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={14} sx={{ color: '#888' }} />
            <Typography fontSize="0.8rem" color="#888">Läser QR-kod...</Typography>
          </Box>
        )}
        {qrStatus === 'found' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <QrCodeScannerIcon sx={{ color: '#4ade80', fontSize: 16 }} />
            <Typography fontSize="0.8rem" color="#4ade80">QR-kod läst — lag och fråga identifierade</Typography>
          </Box>
        )}
        {qrStatus === 'not_found' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <QrCodeScannerIcon sx={{ color: '#888', fontSize: 16 }} />
            <Typography fontSize="0.8rem" color="#888">QR ej läsbar — AI identifierar från bladets text</Typography>
          </Box>
        )}

        {/* Checklista inför fotografering — visas bara innan foto tagits */}
        {!imagePreview && (
          <Paper sx={{ bgcolor: surface, border: `1px solid ${accent}44`, borderRadius: 2, p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
              <InfoOutlinedIcon sx={{ color: accent, fontSize: 16 }} />
              <Typography fontSize="0.78rem" fontWeight={700} color={accent} letterSpacing={0.5}>
                KOLLA IGENOM INNAN DU FOTOGRAFERAR
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { symbol: '—', color: '#facc15', text: 'Har ni satt punkter (–) för att skilja svar och stycken åt?' },
                { symbol: '⭕', color: '#f97316', text: 'Har ni flera långa stycken? Ringa gärna in dem var för sig.' },
                { symbol: '📄', color: '#60a5fa', text: 'Skrivit på baksidan? Kom ihåg att skanna den också efteråt.' },
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Typography
                    fontSize="1.1rem" fontWeight={900}
                    sx={{ lineHeight: 1.4, flexShrink: 0, minWidth: 24, color: item.color }}
                  >
                    {item.symbol}
                  </Typography>
                  <Typography fontSize="0.88rem" color="#ddd" sx={{ lineHeight: 1.5 }}>{item.text}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {/* Photo area */}
        <Paper sx={{ bgcolor: surface, border: '1px solid rgba(84,84,88,0.6)', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
          {imagePreview ? (
            <Box>
              <Box component="img" src={imagePreview} alt="Skannat blad"
                sx={{ width: '100%', maxHeight: 360, objectFit: 'contain', bgcolor: '#111' }} />
              <Box sx={{ display: 'flex', gap: 1, p: 1.5 }}>
                <Button
                  variant="contained" fullWidth onClick={analyze} disabled={analyzing}
                  startIcon={analyzing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AutoAwesomeIcon />}
                  sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 700 }}>
                  {analyzing ? 'AI läser av...' : 'Läs av med AI'}
                </Button>
                <Button component="label" variant="outlined" startIcon={<CameraAltIcon />}
                  sx={{ borderColor: 'rgba(84,84,88,0.6)', color: '#fff', textTransform: 'none', whiteSpace: 'nowrap' }}>
                  Nytt foto
                  <input ref={fileRef} type="file" hidden accept="image/*" capture="environment" onChange={handleFileChange} />
                </Button>
              </Box>
            </Box>
          ) : (
            <Button component="label" fullWidth sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 2, py: 6, color: '#aaa', textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
            }}>
              <CameraAltIcon sx={{ fontSize: 52, color: accent, opacity: 0.8 }} />
              <Box textAlign="center">
                <Typography fontWeight={700} color="#fff" mb={0.3}>Fotografera hela bladet</Typography>
                <Typography fontSize="0.8rem" color="#888">Se till att texten och QR-koden båda syns tydligt</Typography>
              </Box>
              <input ref={fileRef} type="file" hidden accept="image/*" capture="environment" onChange={handleFileChange} />
            </Button>
          )}
        </Paper>

        {/* Verification warning */}
        {verificationWarning && (
          <Alert severity="warning" sx={{ mb: 2, bgcolor: '#2a1800', color: '#ffa040', border: '1px solid #ff8c0044', '& .MuiAlert-icon': { color: '#ffa040' } }}>
            {verificationWarning}
          </Alert>
        )}

        {/* Editable AI results */}
        {parsedAnswers !== null && (
          <Box>
            {/* Deltagare — redigerbar sektion */}
            <Box sx={{ mb: 2.5, px: 1.5, py: 1.5, bgcolor: surface, border: `1px solid ${accent}44`, borderRadius: 1.5 }}>
              <Typography fontSize="0.72rem" color={accent} fontWeight={700} letterSpacing={0.5} mb={1}>
                DELTAGARE
              </Typography>
              {existingGroupNames.length > 0 && participantNames.length === 0 && (
                <Typography fontSize="0.75rem" color="#666" mb={0.75} fontStyle="italic">
                  Namn från andra blad i gruppen: {existingGroupNames.join(', ')}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25, minHeight: 28 }}>
                {participantNames.map((name, i) => (
                  <Chip
                    key={i}
                    label={name}
                    size="small"
                    onDelete={() => setParticipantNames(prev => prev.filter((_, j) => j !== i))}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.1)', color: '#ddd', fontSize: '0.8rem',
                      '& .MuiChip-deleteIcon': { color: '#666', '&:hover': { color: '#ef4444' } },
                    }}
                  />
                ))}
                {participantNames.length === 0 && (
                  <Typography fontSize="0.78rem" color="#555" fontStyle="italic" sx={{ alignSelf: 'center' }}>
                    Inga namn tolkades — lägg till om nödvändigt
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  value={newNameInput}
                  onChange={e => setNewNameInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newNameInput.trim()) {
                      setParticipantNames(prev => [...prev, newNameInput.trim()]);
                      setNewNameInput('');
                    }
                  }}
                  placeholder="Lägg till deltagare..."
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#111', color: '#fff', fontSize: '0.85rem',
                      '& fieldset': { borderColor: 'rgba(84,84,88,0.4)' },
                      '&.Mui-focused fieldset': { borderColor: accent },
                    },
                    '& input::placeholder': { color: '#555', opacity: 1 },
                  }}
                />
                <IconButton
                  onClick={() => {
                    if (newNameInput.trim()) {
                      setParticipantNames(prev => [...prev, newNameInput.trim()]);
                      setNewNameInput('');
                    }
                  }}
                  disabled={!newNameInput.trim()}
                  sx={{ color: accent, '&.Mui-disabled': { color: '#333' } }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <AutoAwesomeIcon sx={{ color: accent, fontSize: 18 }} />
              <Typography fontWeight={700} fontSize="0.9rem">
                {parsedAnswers.length > 0
                  ? `${parsedAnswers.length} svar hittade — granska, redigera och spara`
                  : 'Inga svar hittades i bilden'}
              </Typography>
            </Box>

            {parsedAnswers.length > 0 && (
              <Typography fontSize="0.72rem" color="#666" mb={1.25} sx={{ lineHeight: 1.4 }}>
                Innehåller ett svar flera ord eller fraser? Tryck{' '}
                <ContentCutIcon sx={{ fontSize: 11, verticalAlign: 'middle', color: '#888' }} />{' '}
                för att dela upp dem på separata rader.
              </Typography>
            )}

            {parsedAnswers.map((ans, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1 }}>
                <TextField
                  value={ans} onChange={e => updateAnswer(i, e.target.value)}
                  variant="outlined" size="small" multiline fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: surface, color: '#fff', fontSize: '0.9rem',
                      '& fieldset': { borderColor: 'rgba(84,84,88,0.5)' },
                      '&:hover fieldset': { borderColor: 'rgba(84,84,88,0.9)' },
                      '&.Mui-focused fieldset': { borderColor: accent },
                    },
                  }}
                />
                {canSplit(ans) && (
                  <IconButton size="small" onClick={() => splitAnswer(i)}
                    title="Dela upp på separata rader"
                    sx={{ color: '#facc15', flexShrink: 0, mt: 0.25, '&:hover': { color: '#fde68a' } }}>
                    <ContentCutIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton size="small" onClick={() => removeAnswer(i)} sx={{ color: '#555', flexShrink: 0, mt: 0.25, '&:hover': { color: '#ef4444' } }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            <Button size="small" startIcon={<AddIcon />} onClick={addEmptyAnswer}
              sx={{ color: '#888', textTransform: 'none', mb: 1.5, '&:hover': { color: '#fff' } }}>
              Lägg till svar manuellt
            </Button>

            {!identityKnown && !showManual && (
              <Alert severity="warning" sx={{ mb: 1.5, bgcolor: '#2a1800', color: '#ffa040', border: '1px solid #ff8c0044', '& .MuiAlert-icon': { color: '#ffa040' } }}>
                Kunde inte identifiera lag/fråga automatiskt.{' '}
                <Button size="small" onClick={() => setShowManual(true)} sx={{ color: '#ffa040', textTransform: 'none', p: 0, minWidth: 0, textDecoration: 'underline' }}>
                  Välj manuellt
                </Button>
              </Alert>
            )}

            {identityKnown && parsedAnswers.filter(a => a.trim()).length > 0 && (
              <Button variant="contained" fullWidth onClick={saveAnswers} disabled={saving}
                startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CheckCircleOutlineIcon />}
                sx={{ mt: 0.5, bgcolor: '#4ade80', color: '#000', '&:hover': { bgcolor: '#22c55e' }, textTransform: 'none', fontWeight: 700 }}>
                {saving ? 'Sparar...' : `Spara ${parsedAnswers.filter(a => a.trim()).length} svar`}
              </Button>
            )}
          </Box>
        )}

        {error && (
          <Typography color="#ef4444" fontSize="0.85rem" mt={2} textAlign="center">{error}</Typography>
        )}
      </Box>
    </Box>
  );
}
